import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonialsData();
  }, []);

  const fetchTestimonialsData = async () => {
    try {
      // Fetch testimonials
      const testimonialsResponse = await axios.get(`${API}/testimonials`);
      setTestimonials(testimonialsResponse.data);
      
      // Fetch website stats
      const statsResponse = await axios.get(`${API}/stats`);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching testimonials data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-900 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="text-white text-xl">Loading testimonials... 🔄</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-900 relative overflow-hidden" id="testimonials">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge className="bg-green-100 text-green-800 border-green-200 mb-4 px-4 py-2">
              Testimonials
            </Badge>
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Client Bolte Hain Kya! 🗣️
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Real feedback from real clients jo kaam karwaya hai apun se. Dekh le kitna satisfied hain sab! 💯
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id}
                className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl"
                style={{
                  animationDelay: `${index * 0.2}s`
                }}
              >
                <CardContent className="p-8">
                  {/* Quote Icon */}
                  <div className="text-6xl text-blue-400 mb-4 opacity-20">
                    "
                  </div>

                  {/* Rating Stars */}
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-xl">⭐</span>
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <blockquote className="text-gray-200 text-lg leading-relaxed mb-6 italic">
                    {testimonial.comment}
                  </blockquote>

                  {/* Client Info */}
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-4 ring-2 ring-blue-400">
                      {testimonial.avatar ? (
                        <img 
                          src={testimonial.avatar} 
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                          {testimonial.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-bold text-white text-lg">
                        {testimonial.name}
                      </div>
                      <div className="text-blue-400 text-sm">
                        {testimonial.role}
                      </div>
                      {testimonial.company && (
                        <div className="text-gray-400 text-xs">
                          {testimonial.company}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Verified Badge */}
                  <div className="mt-4">
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                      ✅ Verified Client
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {testimonials.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-300 text-lg">No testimonials available yet. 🤔</div>
            </div>
          )}

          {/* Bottom Stats */}
          {stats && (
            <div className="mt-16 text-center">
              <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-md rounded-2xl p-8 border border-white/10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-black text-white mb-2">
                      {Math.round((stats.total_testimonials / stats.total_contacts) * 100) || 99}%
                    </div>
                    <div className="text-gray-400 text-sm">Client Satisfaction</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-white mb-2">48hr</div>
                    <div className="text-gray-400 text-sm">Average Delivery</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-white mb-2">{stats.total_contacts}+</div>
                    <div className="text-gray-400 text-sm">Happy Clients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-white mb-2">{stats.avg_rating}⭐</div>
                    <div className="text-gray-400 text-sm">Average Rating</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CTA to add testimonial */}
          <div className="mt-12 text-center">
            <Button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-4 text-lg font-semibold transform hover:scale-105 transition-all duration-300"
            >
              Kaam Karwaya Hai? Review De! ⭐
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex justify-center">
            <div className="flex flex-wrap justify-center items-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <span className="text-green-400">✅</span>
                <span className="text-sm">100% Original Work</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-400">🔒</span>
                <span className="text-sm">Secure & Confidential</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-purple-400">⚡</span>
                <span className="text-sm">Fast Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-400">💯</span>
                <span className="text-sm">Money Back Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;